/**
 * MediaSessionService
 *
 * Centralized service to manage the browser's Media Session API for coordinated
 * audio playback control across multiple audio sources (music and ATC).
 *
 * Key features:
 * - Single Media Session API integration point for the app
 * - Coordinates play/pause between music (Howler.js) and ATC (HTML5 Audio)
 * - Handles hardware media keys (headphones, keyboards, etc.)
 * - Handles Bluetooth device events (AirPods disconnect/reconnect)
 * - Provides metadata display in OS media controls and lock screens
 * - Tracks "was playing" state for proper resume behavior
 *
 * Architecture:
 * - Singleton pattern ensures one source of truth for media session
 * - Callbacks registered by music player context for actual playback control
 * - ATC audio element registered by Radar component for coordinated control
 * - All external pause/play events (headphones, notifications) flow through here
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Media_Session_API
 */

import { Logger } from '@/app/utils/logger';
import { unifiedAudioService } from '@services/audio/UnifiedAudioService';
import { Track } from '@/types/MusicPlaylist';

const LOG_TAG = 'media-session';

/**
 * Callbacks for music playback control
 */
export interface MusicPlaybackCallbacks {
  onPlay: () => Promise<void>;
  onPause: () => void;
  onNextTrack?: () => void;
  onPreviousTrack?: () => void;
  onSeekTo?: (time: number) => void;
  onSeekForward?: (offset: number) => void;
  onSeekBackward?: (offset: number) => void;
  onStop?: () => void;
}

/**
 * Current playback state for restoration after Bluetooth reconnection
 */
export interface PlaybackState {
  musicWasPlaying: boolean;
  atcWasPlaying: boolean;
  timestamp: number;
}

/**
 * MediaSessionService class
 *
 * Manages the browser's Media Session API to provide unified playback control
 * for both music and ATC audio sources.
 */
class MediaSessionService {
  private static instance: MediaSessionService | null = null;

  // Registered callbacks for music playback control
  private musicCallbacks: MusicPlaybackCallbacks | null = null;

  // Reference to the ATC audio element for coordinated playback
  private atcAudioRef: HTMLAudioElement | null = null;

  // Reference to the music audio element (for detecting external events)
  private musicAudioRef: HTMLAudioElement | null = null;

  // Track state before external pause (for resume)
  private playbackStateBeforePause: PlaybackState | null = null;

  // Whether the service has been initialized
  private isInitialized = false;

  // Flag to prevent recursive event handling
  private isHandlingExternalEvent = false;

  /**
   * Private constructor - use getInstance() instead
   */
  private constructor() {
    Logger.debug('MediaSessionService instantiated', LOG_TAG);
  }

  /**
   * Get the singleton instance of MediaSessionService
   */
  static getInstance(): MediaSessionService {
    if (!MediaSessionService.instance) {
      MediaSessionService.instance = new MediaSessionService();
    }
    return MediaSessionService.instance;
  }

  /**
   * Reset the singleton instance (mainly for testing)
   */
  static resetInstance(): void {
    if (MediaSessionService.instance) {
      MediaSessionService.instance.destroy();
      MediaSessionService.instance = null;
    }
  }

  /**
   * Check if Media Session API is supported in the current browser
   */
  static isSupported(): boolean {
    return typeof navigator !== 'undefined' && 'mediaSession' in navigator;
  }

  /**
   * Initialize the Media Session service with music playback callbacks
   *
   * @param callbacks - Functions to call when media session actions are triggered
   */
  initialize(callbacks: MusicPlaybackCallbacks): void {
    if (this.isInitialized) {
      Logger.warn('MediaSessionService already initialized, updating callbacks', LOG_TAG);
    }

    this.musicCallbacks = callbacks;

    if (!MediaSessionService.isSupported()) {
      Logger.warn('Media Session API not supported in this browser', LOG_TAG);
      this.isInitialized = true;
      return;
    }

    this.setupActionHandlers();
    this.isInitialized = true;
    Logger.info('MediaSessionService initialized with Media Session API support', LOG_TAG);
  }

  /**
   * Set up Media Session action handlers for play, pause, and navigation
   */
  private setupActionHandlers(): void {
    if (!MediaSessionService.isSupported()) return;

    const actionHandlers: Array<[MediaSessionAction, MediaSessionActionHandler]> = [
      ['play', () => this.handleExternalPlay()],
      ['pause', () => this.handleExternalPause()],
      ['stop', () => this.handleExternalStop()],
      ['nexttrack', () => this.handleNextTrack()],
      ['previoustrack', () => this.handlePreviousTrack()],
      ['seekforward', (details) => this.handleSeekForward(details)],
      ['seekbackward', (details) => this.handleSeekBackward(details)],
      ['seekto', (details) => this.handleSeekTo(details)],
    ];

    for (const [action, handler] of actionHandlers) {
      try {
        navigator.mediaSession.setActionHandler(action, handler);
        Logger.debug(`Registered action handler: ${action}`, LOG_TAG);
      } catch {
        Logger.debug(`Media session action '${action}' not supported`, LOG_TAG);
      }
    }
  }

  /**
   * Handle external play action (from headphones, media keys, notification)
   * Resumes both music and ATC if they were playing before pause
   * 
   * This method is public to allow components to notify the service
   * when they detect external play events that bypass Media Session.
   */
  async handleExternalPlay(): Promise<void> {
    if (this.isHandlingExternalEvent) return;
    this.isHandlingExternalEvent = true;

    Logger.info('External play action received (headphones/media keys)', LOG_TAG);

    try {
      const musicAlreadyPlaying = !!this.musicAudioRef && !this.musicAudioRef.paused;

      // Resume music only when not already started by the browser/OS.
      if (this.musicCallbacks?.onPlay && !musicAlreadyPlaying) {
        await this.musicCallbacks.onPlay();
      } else if (musicAlreadyPlaying) {
        Logger.debug('Skipping onPlay callback because music audio is already playing', LOG_TAG);
      }

      // Resume ATC if it was playing before the pause
      if (this.atcAudioRef && this.playbackStateBeforePause?.atcWasPlaying) {
        Logger.debug('Resuming ATC audio (was playing before pause)', LOG_TAG);
        try {
          await unifiedAudioService.resume();
          await this.atcAudioRef.play();
        } catch (error) {
          Logger.warn('Failed to resume ATC audio', LOG_TAG, {
            error: error instanceof Error ? error.message : String(error),
          });
        }
      }

      // Clear the stored playback state
      this.playbackStateBeforePause = null;

      this.setPlaybackState('playing');
    } finally {
      this.isHandlingExternalEvent = false;
    }
  }

  /**
   * Handle external pause action (from headphones, media keys, notification, AirPods removal)
   * Stores current playback state and pauses both audio sources
   * 
   * This method is public to allow components to notify the service
   * when they detect external pause events that bypass Media Session.
   */
  handleExternalPause(): void {
    if (this.isHandlingExternalEvent) return;
    this.isHandlingExternalEvent = true;

    Logger.info('External pause action received (headphones/media keys/AirPods)', LOG_TAG);

    try {
      // Store current playback state before pausing
      this.playbackStateBeforePause = {
        musicWasPlaying: true, // We know music was playing if pause was triggered
        atcWasPlaying: this.atcAudioRef ? !this.atcAudioRef.paused : false,
        timestamp: Date.now(),
      };

      Logger.debug('Stored playback state before pause', LOG_TAG, this.playbackStateBeforePause);

      // Pause music
      if (this.musicCallbacks?.onPause) {
        this.musicCallbacks.onPause();
      }

      // Pause ATC
      if (this.atcAudioRef && !this.atcAudioRef.paused) {
        Logger.debug('Pausing ATC audio', LOG_TAG);
        this.atcAudioRef.pause();
      }

      this.setPlaybackState('paused');
    } finally {
      this.isHandlingExternalEvent = false;
    }
  }

  /**
   * Handle external stop action
   */
  private handleExternalStop(): void {
    Logger.info('External stop action received', LOG_TAG);

    if (this.musicCallbacks?.onStop) {
      this.musicCallbacks.onStop();
    } else if (this.musicCallbacks?.onPause) {
      this.musicCallbacks.onPause();
    }

    if (this.atcAudioRef) {
      this.atcAudioRef.pause();
      this.atcAudioRef.currentTime = 0;
    }

    this.setPlaybackState('none');
  }

  /**
   * Handle next track action
   */
  private handleNextTrack(): void {
    Logger.debug('External next track action received', LOG_TAG);
    this.musicCallbacks?.onNextTrack?.();
  }

  /**
   * Handle previous track action
   */
  private handlePreviousTrack(): void {
    Logger.debug('External previous track action received', LOG_TAG);
    this.musicCallbacks?.onPreviousTrack?.();
  }

  /**
   * Handle seek forward action
   */
  private handleSeekForward(details: MediaSessionActionDetails): void {
    const offset = details.seekOffset ?? 10;
    Logger.debug(`External seek forward: ${offset}s`, LOG_TAG);
    this.musicCallbacks?.onSeekForward?.(offset);
  }

  /**
   * Handle seek backward action
   */
  private handleSeekBackward(details: MediaSessionActionDetails): void {
    const offset = details.seekOffset ?? 10;
    Logger.debug(`External seek backward: ${offset}s`, LOG_TAG);
    this.musicCallbacks?.onSeekBackward?.(offset);
  }

  /**
   * Handle seek to specific time action
   */
  private handleSeekTo(details: MediaSessionActionDetails): void {
    const time = details.seekTime ?? 0;
    Logger.debug(`External seek to: ${time}s`, LOG_TAG);
    this.musicCallbacks?.onSeekTo?.(time);
  }

  /**
   * Register the ATC audio element for coordinated playback control
   * Should be called by the Radar component when the audio element is ready
   *
   * @param audioElement - The ATC HTMLAudioElement to coordinate with
   */
  setAtcAudio(audioElement: HTMLAudioElement | null): void {
    if (this.atcAudioRef === audioElement) return;

    this.atcAudioRef = audioElement;
    Logger.debug('ATC audio element registered with MediaSessionService', LOG_TAG, {
      hasElement: !!audioElement,
    });
  }

  /**
   * Get the currently registered ATC audio element (if any).
   * Useful when coordinating playback from a user gesture (e.g. clicking Play).
   */
  getAtcAudio(): HTMLAudioElement | null {
    return this.atcAudioRef;
  }

  /**
   * Register the music audio element (used by Howler.js in HTML5 mode)
   * This allows detecting external events on the music audio element
   *
   * @param audioElement - The music HTMLAudioElement (from Howler.js)
   */
  setMusicAudio(audioElement: HTMLAudioElement | null): void {
    if (this.musicAudioRef === audioElement) return;

    this.musicAudioRef = audioElement;
    Logger.debug('Music audio element registered with MediaSessionService', LOG_TAG, {
      hasElement: !!audioElement,
    });
  }

  /**
   * Update media metadata for display in OS media controls
   * Call this when the current track changes
   *
   * @param track - The current track being played
   */
  updateMetadata(track: Track | null): void {
    if (!MediaSessionService.isSupported() || !track) {
      return;
    }

    try {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: track.title || 'Unknown Track',
        artist: track.artist || 'Chill Airwaves',
        album: track.metadata?.album || 'Lo-Fi Radio',
        artwork: track.coverUrl ? [{ src: track.coverUrl }] : [],
      });

      Logger.debug('Updated media session metadata', LOG_TAG, { title: track.title });
    } catch (error) {
      Logger.warn('Failed to update media session metadata', LOG_TAG, {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  /**
   * Update the playback state displayed in OS media controls
   *
   * @param state - 'playing', 'paused', or 'none'
   */
  setPlaybackState(state: 'playing' | 'paused' | 'none'): void {
    if (!MediaSessionService.isSupported()) return;

    try {
      navigator.mediaSession.playbackState = state;
      Logger.debug(`Set playback state: ${state}`, LOG_TAG);
    } catch (error) {
      Logger.warn('Failed to set playback state', LOG_TAG, {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  /**
   * Update the position state for seekable media controls
   *
   * @param duration - Total duration in seconds
   * @param position - Current position in seconds
   * @param playbackRate - Playback rate (default 1.0)
   */
  setPositionState(duration: number, position: number, playbackRate = 1.0): void {
    if (!MediaSessionService.isSupported()) return;

    // Validate inputs to prevent errors
    if (duration <= 0 || position < 0 || position > duration) {
      return;
    }

    try {
      navigator.mediaSession.setPositionState({
        duration,
        position,
        playbackRate,
      });
    } catch {
      // Position state errors are common and usually harmless
      Logger.debug('Failed to set position state', LOG_TAG);
    }
  }

  /**
   * Alias for setPositionState with swapped parameter order for convenience
   * Use this from the music player context where currentTime is naturally first
   *
   * @param position - Current position in seconds
   * @param duration - Total duration in seconds
   * @param playbackRate - Playback rate (default 1.0)
   */
  updatePositionState(position: number, duration: number, playbackRate = 1.0): void {
    this.setPositionState(duration, position, playbackRate);
  }

  /**
   * Clear the position state (when track ends or is unloaded)
   */
  clearPositionState(): void {
    if (!MediaSessionService.isSupported()) return;

    try {
      navigator.mediaSession.setPositionState();
    } catch {
      // Ignore errors when clearing
    }
  }

  /**
   * Notify the service that playback has started internally (not from external action)
   * This allows proper state tracking without triggering recursive handlers
   */
  notifyPlaybackStarted(): void {
    this.setPlaybackState('playing');
    // Clear any stored pause state since playback is now active
    this.playbackStateBeforePause = null;
  }

  /**
   * Notify the service that playback has paused internally (not from external action)
   * Stores the ATC state for potential resume
   */
  notifyPlaybackPaused(): void {
    if (!this.isHandlingExternalEvent) {
      // Store ATC state for potential external resume
      this.playbackStateBeforePause = {
        musicWasPlaying: false, // Music is being paused intentionally
        atcWasPlaying: this.atcAudioRef ? !this.atcAudioRef.paused : false,
        timestamp: Date.now(),
      };
    }
    this.setPlaybackState('paused');
  }

  /**
   * Get the stored playback state (useful for debugging/testing)
   */
  getPlaybackStateBeforePause(): PlaybackState | null {
    return this.playbackStateBeforePause;
  }

  /**
   * Check if the service is handling an external event
   * Useful to prevent recursive event handling
   */
  isProcessingExternalEvent(): boolean {
    return this.isHandlingExternalEvent;
  }

  /**
   * Clean up the service (remove handlers, clear references)
   */
  destroy(): void {
    if (MediaSessionService.isSupported()) {
      // Remove all action handlers
      const actions: MediaSessionAction[] = [
        'play',
        'pause',
        'stop',
        'nexttrack',
        'previoustrack',
        'seekforward',
        'seekbackward',
        'seekto',
      ];

      for (const action of actions) {
        try {
          navigator.mediaSession.setActionHandler(action, null);
        } catch {
          // Ignore errors when removing handlers
        }
      }
    }

    this.musicCallbacks = null;
    this.atcAudioRef = null;
    this.playbackStateBeforePause = null;
    this.isInitialized = false;

    Logger.info('MediaSessionService destroyed', LOG_TAG);
  }
}

// Export singleton accessor
export const getMediaSessionService = (): MediaSessionService => {
  return MediaSessionService.getInstance();
};

// Export class for testing
export { MediaSessionService };
